import { css, styled } from 'styled-components';

import { ValidateCircleOutlinedIcon } from '../../Icon/icons';

import type { ControlProps } from './types';

// todo: add install instruction for IE 11 to support :focus-within via the polyfill https://github.com/matteobad/focus-within-polyfill
// todo: Test that the polyfill actually works…
const containerFocus = css<ControlProps>`
  box-shadow: 0 0 0 ${({ theme }) => theme.designTokens.controlFocusShadowSpread}
    ${({ errored, theme }) =>
      errored ? theme.designTokens.controlErroredFocusShadowColor : theme.designTokens.controlFocusShadowColor};
`;

const Container = styled.div<ControlProps>`
  display: block;
  position: relative;
  border-radius: ${({ theme }) => theme.designTokens.controlBorderRadius};
  box-shadow: 0 0 0 0 ${({ theme }) => theme.designTokens.controlFocusShadowColor};
  transition: box-shadow ${({ theme }) => theme.designTokens.controlFocusShadowTransitionTime}
    ${({ theme }) => theme.designTokens.controlFocusShadowTransitionFunction};

  &:focus-within,
  &.focus-within {
    ${({ disabled }) => !disabled && containerFocus}
  }

  textarea {
    resize: vertical;
  }
`;

const controlReset = css`
  border: none;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  width: 100%;
  &:focus {
    outline: none;
  }
`;

const controlDefault = css<ControlProps>`
  display: block;
  vertical-align: middle;
  text-overflow: ellipsis;
  letter-spacing: ${({ theme }) => theme.designTokens.controlLetterSpacing};
  color: ${({ theme }) => theme.designTokens.controlTextColor};
  background: ${({ theme }) => theme.designTokens.controlBackgroundColor};
  font-family: ${({ theme }) => theme.designTokens.controlFontFamily};
  font-size: ${({ theme }) => theme.designTokens.controlFontSize};
  line-height: ${({ theme }) => theme.designTokens.controlLineHeight};
  font-weight: ${({ theme }) => theme.designTokens.controlFontWeight};
  padding: ${({ theme }) => theme.designTokens.controlPaddingY} ${({ theme }) => theme.designTokens.controlPaddingX};
  border-radius: ${({ theme }) => theme.designTokens.controlBorderRadius};
  border: ${({ theme }) => theme.designTokens.controlBorderWidth} solid
    ${({ errored, theme }) =>
      errored ? theme.designTokens.controlErroredBorderColor : theme.designTokens.controlBorderColor};

  &::placeholder {
    color: ${({ theme }) => theme.designTokens.controlPlaceholderTextColor};
  }
`;

const controlHover = css`
  border-color: ${({ theme }) => theme.designTokens.controlHoverBorderColor};
`;

const controlFocus = css`
  border-color: ${({ theme }) => theme.designTokens.controlFocusBorderColor};
  box-shadow: 0 0 0 ${({ theme }) => theme.designTokens.controlFocusBorderAdditionalWidth}
    ${({ theme }) => theme.designTokens.controlFocusBorderColor};
`;
const controlErroredFocus = css`
  border-color: ${({ theme }) => theme.designTokens.controlErroredBorderColor};
  box-shadow: 0 0 0 ${({ theme }) => theme.designTokens.controlFocusBorderAdditionalWidth}
    ${({ theme }) => theme.designTokens.controlErroredBorderColor};
`;

const controlDisabled = css`
  border-color: ${({ theme }) => theme.designTokens.controlDisabledBorderColor};
  box-shadow: 0 0 0 ${({ theme }) => theme.designTokens.controlDisabledBorderAdditionalWidth}
    ${({ theme }) => theme.designTokens.controlDisabledBorderColor};
  background: ${({ theme }) => theme.designTokens.controlDisabledBackgroundColor};
  color: ${({ theme }) => theme.designTokens.controlDisabledTextColor};
`;

const controlWithPrefix = css<ControlProps>`
  padding-left: ${({ theme }) => theme.designTokens.controlPrefixSuffixSlotWidth};
`;
const controlWithSuffix = css<ControlProps>`
  padding-right: ${({ theme }) => theme.designTokens.controlPrefixSuffixSlotWidth};
`;

const propsToRemoveFromDOM = new Set(['prefix', 'suffix', 'errored', 'valid']);
const Control = styled.div.withConfig({
  shouldForwardProp: prop => !propsToRemoveFromDOM.has(prop),
})<ControlProps>`
  ${controlReset}
  ${controlDefault}
  &:hover,
  &.hover {
    ${({ disabled, errored }) => !disabled && !errored && controlHover}
  }
  &:focus,
  &.focus {
    ${({ disabled, errored }) => !disabled && (errored ? controlErroredFocus : controlFocus)}
  }
  ${({ disabled }) => disabled && controlDisabled}
  ${({ prefix }) => prefix && controlWithPrefix}
  ${({ suffix, valid }) => (suffix || valid) && controlWithSuffix}
`;

type ControlIconProps = Pick<ControlProps, 'disabled'> & { placement: 'left' | 'right' };
const ControlPrefixSuffix = styled.div<ControlIconProps>`
  top: 50%;
  display: flex;
  position: absolute;
  align-items: center;
  pointer-events: none;
  justify-content: center;
  transform: translateY(-50%);
  color: ${({ disabled, theme }) =>
    disabled ? theme.designTokens.controlDisabledTextColor : theme.designTokens.controlTextColor};
  ${({ placement }) => placement}: calc(${({ theme }) => theme.designTokens.controlPrefixSuffixPaddingX} + 1px);
`;
const ControlValidIcon = styled(ValidateCircleOutlinedIcon)<ControlIconProps>`
  color: ${({ disabled, theme }) =>
    disabled ? theme.designTokens.controlDisabledTextColor : theme.designTokens.controlValidIconColor};
`;

export const Styled = {
  Container,
  Control,
  PrefixSuffix: ControlPrefixSuffix,
  ValidIcon: ControlValidIcon,
};
